import { useState } from 'react';
import styled from 'styled-components';
import { TimbaStanding } from '../api/client';
import { rankStandings, RankedStanding } from '../lib/ranking';

// Tabla de posiciones reutilizable. Muestra el top `top` (medallas a los 3 primeros) y, si el
// jugador no entra en ese top, agrega su fila abajo con su puesto real. Resalta su fila (filete).
export function TimbaBoard({
  standings,
  pub,
  top = 10,
  provisional = false,
  onDelete,
}: {
  standings: TimbaStanding[];
  pub: string;
  top?: number;
  provisional?: boolean; // el ranking cuenta el parcial de un partido en vivo → puede moverse
  onDelete?: (pub: string, nick: string) => void; // admin: borrar jugador (identidad fantasma)
}) {
  const [showAll, setShowAll] = useState(false);
  if (standings.length === 0) {
    return <Empty>Todavía no hay pronósticos. ¡Sé el primero!</Empty>;
  }
  // Puesto con empates (mismo puntaje = mismo puesto) + el usuario logueado primero en su puesto.
  const ranked = rankStandings(standings, pub);
  const medal = (pos: number) => (pos <= 3 ? ['🥇', '🥈', '🥉'][pos - 1] : pos);
  // groupStart = primer jugador de un puesto: solo ahí se muestra el trofeo/número y la línea
  // separadora. Los demás del mismo puesto van sin marca (puesto una sola vez por grupo).
  const row = (r: RankedStanding, groupStart: boolean) => (
    <li key={r.pub} className={`${r.pub === pub ? 'me' : ''}${groupStart ? ' groupStart' : ''}`}>
      <span className="pos">{groupStart ? medal(r.position) : ''}</span>
      <span className="who">{r.nick}{r.pub === pub ? ' (vos)' : ''}</span>
      <span className="meta">{r.exacts} exactos · {r.played} jug.</span>
      <span className="pts">{r.points}</span>
      {onDelete && (
        <button type="button" className="del" title="Borrar jugador" onClick={() => onDelete(r.pub, r.nick)}>🗑</button>
      )}
    </li>
  );
  const visible = showAll ? ranked : ranked.slice(0, top);
  const myIdx = ranked.findIndex((r) => r.pub === pub);
  const showMyRow = !showAll && myIdx >= top; // si quedé fuera del top, mi fila va aparte abajo
  return (
    <>
      {provisional && (
        <Live>
          <span className="dot" />
          <span>En vivo · puntaje provisional (cuenta el partido en curso)</span>
        </Live>
      )}
      <Ol className="scroll">
        {visible.map((r, i) => row(r, i === 0 || visible[i - 1].position !== r.position))}
      </Ol>
      {showMyRow && <Ol className="meRow">{row(ranked[myIdx], true)}</Ol>}
      {ranked.length > top && (
        <SeeAll type="button" onClick={() => setShowAll((v) => !v)}>
          {showAll ? `Ver solo el top ${top}` : `Ver ranking completo (${ranked.length})`}
        </SeeAll>
      )}
    </>
  );
}

const Live = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent};
    animation: livePulse 1.4s infinite;
  }
  @keyframes livePulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
  }
`;

const Empty = styled.p`
  padding: 16px 14px;
  margin: 0;
  font-size: 0.94rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SeeAll = styled.button`
  width: 100%;
  background: none;
  color: ${({ theme }) => theme.colors.accent};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: 12px;
  font-weight: 700;
  font-size: 0.95rem;
`;

const Ol = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  &.scroll { max-height: 396px; overflow-y: auto; }
  &.meRow { margin-top: 12px; }
  &.meRow::before {
    content: 'Tu posición';
    display: block;
    font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textMuted};
    margin: 0 0 6px 4px;
  }
  li { display: flex; align-items: center; gap: 10px; padding: 11px 14px; border-top: none; }
  /* Línea separadora SOLO entre puestos (arriba del 1er jugador de cada puesto), no entre cada jugador.
     La fila propia (.me) ya se separa sola por ser una card con margen, así que no lleva la línea. */
  li.groupStart:not(:first-child):not(.me) { border-top: 1px solid ${({ theme }) => theme.colors.borderStrong}; }

  /* Mi fila = card bien destacada (en el top o, si estoy más lejos, debajo del 10). */
  li.me {
    background: ${({ theme }) => theme.colors.accent};
    border: none;
    border-radius: 12px;
    margin: 6px;
    padding: 13px 14px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
  }
  li.me .pos, li.me .who, li.me .meta, li.me .pts { color: ${({ theme }) => theme.colors.accentText}; }
  li.me .meta { opacity: 0.85; }
  li.me .pts { font-size: 1.46rem; }

  .pos { width: 28px; font-size: 1.11rem; font-weight: 800; text-align: center; color: ${({ theme }) => theme.colors.textMuted}; }
  .who { flex: 1; min-width: 0; font-weight: 700; font-size: 1.04rem; }
  .meta { font-size: 0.8rem; color: ${({ theme }) => theme.colors.textMuted}; white-space: nowrap; }
  .pts { font-weight: 900; font-size: 1.21rem; color: ${({ theme }) => theme.colors.accent}; min-width: 32px; text-align: right; }
  .del { flex: 0 0 auto; background: none; border: none; font-size: 1rem; opacity: 0.6; cursor: pointer; padding: 0 2px; }
  .del:hover { opacity: 1; }
`;
